// HTTP client for /admin/fatigue/*.
//
// Wire shapes only — no tiering, no derived copy. Those are attached in
// ./fatigueMapper so the provisional RAG bands in ./methodology stay the one
// place they are defined (PEAK-185).

import type { RotaData, RotaImportResult } from './rota';
import { apiClient } from '../../../../../lib/axios';
import { buildQueryParams } from '../../../../../shared/api/metrics';
import type { MetricsFilters } from '../../../../../shared/api/types';
import type { ShiftType } from './types';
import type { WireWorkforce } from '../dashboard/engine/live';

/**
 * PEAK-263 — the generic filter set (region/depot/department/job role/age/
 * sex/shift pattern/reliable-data-only) every other dashboard sends, so
 * `/admin/fatigue/*` is filtered the same way as the rest of the admin suite.
 * `loadFatigueContext` on the API already reads this shape via
 * `resolveFilteredUserIds` — only the client never sent it before.
 */
export type FatigueQueryParams = MetricsFilters;

export interface ApiFatigueWindow {
  start: string;
  end: string;
  days: number;
}

export interface ApiCompliance {
  cohortId: string;
  cohortName: string;
  activeUsers: number;
  enrolledUsers: number;
  validDataDays: number;
  missingDataDays: number;
  coveragePct: number;
  confidenceScore: number;
}

export interface ApiCohortRow {
  cohort: {
    id: string;
    name: string;
    sector: string;
    site: string;
    shiftType: ShiftType;
    headcount: number;
  };
  fedPer1000: number;
  fedPctWorkdays: number;
  lrmPct: number;
  cdiScore: number;
  pdcPct: number;
  trend: 'up' | 'down' | 'stable';
  sparkline: number[];
  compliance: ApiCompliance;
  suppressed: boolean;
  noData: boolean;
}

export interface ApiFatigueSummary {
  window: ApiFatigueWindow;
  groupBy: string;
  metrics: { fed: number; lrm: number; cdi: number; pdc: number };
  empty: boolean;
  coverage: ApiCompliance;
  cohorts: ApiCohortRow[];
  trend: { period: string; fed: number; lrm: number; cdi: number; pdc: number; empty: boolean }[];
  caveats: {
    workdaysAreCoveredDays: boolean;
    shiftTypeInferred: boolean;
    minCohortSize: number;
    reliableConfidenceMin: number;
    suppressedCohorts: string[];
  };
}

export interface ApiCohortDaily {
  cohort: ApiCohortRow['cohort'];
  window: ApiFatigueWindow;
  seriesStart: string;
  series: {
    day: string;
    contributors: number;
    sleepHours: number | null;
    sleepDebtHours: number | null;
    readiness: number | null;
    strain: number | null;
    fedPct: number | null;
    lrmPct: number | null;
  }[];
  metrics: { fed: number; lrm: number; cdi: number; pdc: number } | null;
  suppressed: boolean;
}

export interface ApiIndividual {
  userId: string;
  name: string;
  email: string | null;
  jobRole: string | null;
  score: number | null;
  dayCount: number;
  fedPct: number | null;
  lrmPct: number | null;
  cdiScore: number | null;
  inPdc: boolean | null;
  avgSleepHours: number | null;
  avgReadiness: number | null;
  avgStrain: number | null;
}

export interface ApiIndividuals {
  cohort: { id: string; name: string; isUnassigned?: boolean; headcount?: number };
  window: ApiFatigueWindow;
  individuals: ApiIndividual[];
}

const buildParams = (params: FatigueQueryParams) => buildQueryParams(params);

export const fatigueApi = {
  getRota: async (clientId: string): Promise<RotaData> =>
    (await apiClient.get('/admin/fatigue/rota', { params: { client_id: clientId } })).data,
  importRota: async (clientId: string, csv: string, dryRun: boolean): Promise<RotaImportResult> =>
    (await apiClient.post('/admin/fatigue/rota/import', { csv, dry_run: dryRun }, { params: { client_id: clientId } }))
      .data,
  getWorkforce: async (clientId: string): Promise<WireWorkforce> => {
    const response = await apiClient.get('/admin/fatigue/workforce', { params: { client_id: clientId } });
    return response.data;
  },

  getSummary: async (params: FatigueQueryParams): Promise<ApiFatigueSummary> => {
    const response = await apiClient.get('/admin/fatigue/summary', { params: buildParams(params) });
    return response.data;
  },

  getCohortDaily: async (cohortId: string, params: FatigueQueryParams): Promise<ApiCohortDaily> => {
    const response = await apiClient.get(`/admin/fatigue/cohorts/${encodeURIComponent(cohortId)}/daily`, {
      params: buildParams(params),
    });
    return response.data;
  },

  /**
   * Individual-level detail (FM-8/FM-9). The API rejects this for anyone but a
   * central admin on a non-anonymised client, so a 403 here is expected rather
   * than exceptional — callers gate on useIndividualAccess first (FM-10).
   */
  getCohortIndividuals: async (cohortId: string, params: FatigueQueryParams): Promise<ApiIndividuals> => {
    const response = await apiClient.get(`/admin/fatigue/cohorts/${encodeURIComponent(cohortId)}/individuals`, {
      params: buildParams(params),
    });
    return response.data;
  },
};
